/**
 * MIDI Compatibility Helper
 * Provides cross-browser MIDI support with polyfill for Safari
 */

// Browser detection
export const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  
  if (ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('Chromium')) {
    return { name: 'Safari', needsExtension: true };
  }
  if (ua.includes('Firefox')) {
    const version = parseInt(ua.match(/Firefox\/(\d+)/)?.[1] || '0');
    return { name: 'Firefox', needsExtension: version < 108, version };
  }
  if (ua.includes('Edg/')) {
    return { name: 'Edge', needsExtension: false };
  }
  if (ua.includes('Chrome')) {
    return { name: 'Chrome', needsExtension: false };
  }
  if (ua.includes('Opera') || ua.includes('OPR/')) {
    return { name: 'Opera', needsExtension: false };
  }
  
  return { name: 'Unknown', needsExtension: true };
};

// Check if Web MIDI API is available
export const checkMIDISupport = () => {
  return typeof navigator !== 'undefined' && 'requestMIDIAccess' in navigator;
};

// Extension download links
export const EXTENSION_LINKS = {
  safari: {
    jazzMidi: 'https://apps.apple.com/app/jazz-midi/id1506447231',
    webMidiApi: 'https://apps.apple.com/app/web-midi-browser/id953846217',
    instructions: [
      'Install "Jazz-MIDI" from the Mac App Store',
      'Install "Web MIDI Browser" extension from the Mac App Store',
      'Enable both extensions in Safari → Settings → Extensions',
      'Refresh this page'
    ]
  },
  firefox: {
    jazzMidi: 'https://addons.mozilla.org/firefox/addon/jazz-midi/',
    webMidiApi: 'https://addons.mozilla.org/firefox/addon/web-midi-api/',
    instructions: [
      'Install "Jazz-MIDI" Firefox add-on',
      'Install "Web MIDI API" Firefox add-on',
      'Restart Firefox',
      'Refresh this page'
    ]
  }
};

// Get setup instructions based on browser
export const getSetupInstructions = (browser) => {
  if (browser.name === 'Safari') {
    return {
      title: 'Safari MIDI Setup Required',
      subtitle: 'Safari needs a free extension to support MIDI devices',
      steps: EXTENSION_LINKS.safari.instructions,
      links: [
        { label: 'Jazz-MIDI Extension', url: EXTENSION_LINKS.safari.jazzMidi },
        { label: 'Web MIDI Browser', url: EXTENSION_LINKS.safari.webMidiApi }
      ],
      alternative: 'Or use Chrome/Firefox for native MIDI support'
    };
  }
  
  if (browser.name === 'Firefox' && browser.needsExtension) {
    return {
      title: 'Firefox MIDI Setup',
      subtitle: 'Your Firefox version needs an extension for MIDI support',
      steps: EXTENSION_LINKS.firefox.instructions,
      links: [
        { label: 'Jazz-MIDI Add-on', url: EXTENSION_LINKS.firefox.jazzMidi },
        { label: 'Web MIDI API Add-on', url: EXTENSION_LINKS.firefox.webMidiApi }
      ],
      alternative: 'Or update Firefox to version 108+ for native support'
    };
  }
  
  return null;
};

// Status messages
export const getMIDIStatusMessage = (browser, hasSupport) => {
  if (hasSupport) {
    return {
      type: 'success',
      message: 'MIDI is supported in your browser',
      icon: 'check'
    };
  }
  
  if (browser.name === 'Safari') {
    return {
      type: 'warning',
      message: 'Safari requires a free extension for MIDI support',
      icon: 'alert'
    };
  }
  
  if (browser.name === 'Firefox' && browser.needsExtension) {
    return {
      type: 'warning', 
      message: 'Your Firefox version needs an extension or update for MIDI',
      icon: 'alert'
    };
  }
  
  return {
    type: 'error',
    message: 'MIDI may not be supported in your browser',
    icon: 'error'
  };
};

export default {
  getBrowserInfo,
  checkMIDISupport,
  getSetupInstructions,
  getMIDIStatusMessage,
  EXTENSION_LINKS
};
